import { Box } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import Toast from 'components/Toast';
import CheckboxInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/CheckboxInputPropertiesSubForm';
import ConstantInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/ConstantInputPropertiesSubForm';
import ComparatorInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/ComparatorInputPropertiesSubForm';
import DatePlusPeriodInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/DatePlusPeriodInputPropertiesSubForm';
import DeviceEvidenceFieldInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/DeviceEvidenceFieldPropertiesSubForm';
import DevicePropertyInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/DevicePropertyInputPropertiesSubForm';
import FieldUpdatedAtInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/FieldUpdatedAtInputPropertiesSubForm';
import FileListInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/FileListInputPropertiesSubForm';
import IfElseInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/IfElseInputPropertiesSubForm';
import DeviceFieldAggregatorInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/DeviceFieldAggregatorInputPropertiesSubForm';
import MeasurementColumnAggregatorInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/MeasurementColumnAggregatorInputPropertiesSubForm';
import TextExpressionInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/TextExpressionInputPropertiesSubForm';
import NumberInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/NumberInputPropertiesSubForm';
import NumberOfMeasurementsInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/NumberOfMeasurementsInputPropertiesSubForm';
import PredefinedListInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/PredefinedListInputPropertiesSubForm';
import PredefinedSelectInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/PredefinedSelectInputPropertiesSubForm';
import SafetyPointInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/SafetyPointInputPropertiesSubForm';
import SelectInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/SelectInputPropertiesSubForm';
import SumInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/SumInputPropertiesSubForm';
import ValueMapInputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/properties/ValueMapInputPropertiesSubForm';
import {
  IFormInputContext,
  IFormInputSpec,
  IFormInputSpecFileList,
  IFormInputType,
} from 'pages/revisions-module/template-editor/editors/form/types';

type TypeCond<T, U> = [T] extends [never] ? { message: U } : {};

/**
 * Parameters of the InputPropertiesSubForm component.
 */
interface InputPropertiesSubFormProps {
  type: IFormInputType;
  form: UseFormReturnType<IFormInputSpec>;
  context?: IFormInputContext;
}

/**
 * Used to configure the type-specific properties of an input.
 */
export default function InputPropertiesSubForm({ type, form, context = 'revision' }: InputPropertiesSubFormProps) {
  switch (type) {
    case 'text':
      return <></>;

    case 'number':
      return <NumberInputPropertiesSubForm form={form} />;

    case 'date':
      return <></>;

    case 'checkbox':
      return <CheckboxInputPropertiesSubForm form={form} />;

    case 'certificate':
      return <></>;

    case 'qualification':
      return <></>;

    case 'select':
      return <SelectInputPropertiesSubForm form={form} />;

    case 'wysiwyg':
      return <></>;

    case 'constant':
      return <ConstantInputPropertiesSubForm form={form} />;

    case 'valueMap':
      return <ValueMapInputPropertiesSubForm form={form} context={context} />;

    case 'ifElse':
      return <IfElseInputPropertiesSubForm form={form} context={context} />;

    case 'comparator':
      return <ComparatorInputPropertiesSubForm form={form} context={context} />;

    case 'predefinedSelect':
      return <PredefinedSelectInputPropertiesSubForm form={form} />;

    case 'predefinedList':
      return <PredefinedListInputPropertiesSubForm form={form} />;

    case 'measuringDevice':
      return <></>;

    case 'period':
      return <></>;

    case 'safetyPoint':
      return <SafetyPointInputPropertiesSubForm form={form} />;

    case 'deviceProperty':
      return <DevicePropertyInputPropertiesSubForm form={form} />;

    case 'deviceEvidenceField':
      return <DeviceEvidenceFieldInputPropertiesSubForm form={form} />;

    case 'sum':
      return <SumInputPropertiesSubForm form={form} context={context} />;

    case 'datePlusPeriod':
      return <DatePlusPeriodInputPropertiesSubForm form={form} />;

    case 'faultType':
      return <></>;

    case 'faultSeverity':
      return <></>;

    case 'numberOfMeasurements':
      return <NumberOfMeasurementsInputPropertiesSubForm form={form} />;

    case 'measurementColumnAggregator':
      return <MeasurementColumnAggregatorInputPropertiesSubForm form={form} />;

    case 'deviceFieldAggregator':
      return <DeviceFieldAggregatorInputPropertiesSubForm form={form} />;

    case 'textExpression':
      return <TextExpressionInputPropertiesSubForm form={form} context={context} />;

    case 'fieldUpdatedAt':
      return <FieldUpdatedAtInputPropertiesSubForm form={form} context={context} />;

    case 'fileList':
      return <FileListInputPropertiesSubForm form={form as UseFormReturnType<IFormInputSpecFileList>} />;

    default:
      // This is a TS trick to make sure all types are handled in the switch.
      // If there is a new type, the compiler will complain here. To fix it, add a new case to the switch.
      const { message }: TypeCond<typeof type, string> = { message: 'Neznámy typ poľa' };

      return (
        <Box mr={32}>
          <Toast fullWidth type="fail" withCloseButton={false} message={message} />
        </Box>
      );
  }
}
